import { useMemo, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useImmer } from 'use-immer'
import { Button, Flex, FormControl, FormErrorMessage, FormLabel, Grid, Image, Input, Select, VStack } from '@chakra-ui/react'
import { getAssetUrl, ROLENAMES, ROLES } from 'lib'

export const DEFAULT_PHOTO_URL = 'users/images/avatar.png'

export const UserFormType = {
  Add: 'add',
  Edit: 'edit',
  Profile: 'profile'
}

export const UserForm = ({ type, onSubmit, isSubmitting, initialForm = null, initialFormError = null, isToggling = false, onToggleLock = null }) => {
  const photoRef = useRef(null)
  const [form, setForm] = useImmer({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    image: DEFAULT_PHOTO_URL,
    role: '',
    password: '',
    passwordConfirmation: '',
    active: 1
  })
  const [formError, setFormError] = useImmer({
    email: '',
    password: ''
  })
  const [photo, setPhoto] = useState(getAssetUrl(initialForm?.image || DEFAULT_PHOTO_URL))

  const buttonText = useMemo(() => {
    switch (type) {
      case UserFormType.Add:
        return 'Add User'
      case UserFormType.Edit:
        return 'Save User'
      case UserFormType.Profile:
        return 'Save Profile'
    }
  }, [type])

  useEffect(() => {
    setForm(draft => {
      Object.keys(initialForm || {}).forEach(k => { draft[k] = initialForm[k] ?? '' })
    })
  }, [initialForm])

  useEffect(() => {
    setFormError(draft => {
      Object.keys(initialFormError || {}).forEach(k => { draft[k] = initialFormError[k] || '' })
    })
  }, [initialFormError])

  const handleChange = (e) => {
    setForm(draft => {
      draft[e.target.name] = e.target.value
    })

    if (formError[e.target.name]) {
      setFormError(draft => { draft[e.target.name] = '' })
    }
  }

  const handleChangePhoto = (e) => {
    const file = e.target.files?.[0]

    if (file) {
      setForm(draft => {
        draft[e.target.name] = e.target.files[0]
      })

      const reader = new FileReader()
      reader.onload = () => {
        setPhoto(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleRemovePhoto = () => {
    setForm(draft => { draft.image = DEFAULT_PHOTO_URL })
    setPhoto(getAssetUrl(DEFAULT_PHOTO_URL))
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (type === UserFormType.Add) {
      if (form.password !== form.passwordConfirmation) {
        setFormError(draft => { draft.password = 'Password does not match.' })
        return
      }
    }

    onSubmit(form)
  }

  return (
    <form onSubmit={handleSubmit}>
      <Grid
        templateColumns={type === UserFormType.Profile ? 'repeat(2, 1fr)' : '1fr'}
      >
        <VStack gap={2} maxW="500px">
          <FormControl isRequired>
            <FormLabel>Role</FormLabel>
            <Select name="role" value={form.role} onChange={handleChange} placeholder="Select role">
              {Object.values(ROLES).filter(v => v !== ROLES.SuperUser).map(v => <option key={v} value={v}>{ROLENAMES[v]}</option>)}
            </Select>
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Firstname</FormLabel>
            <Input name="firstname" value={form.firstname} onChange={handleChange} />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Lastname</FormLabel>
            <Input name="lastname" value={form.lastname} onChange={handleChange} />
          </FormControl>
          <FormControl isInvalid={formError.email} isRequired>
            <FormLabel>Email</FormLabel>
            <Input type="email" name="email" value={form.email} onChange={handleChange} />
            {formError.email && <FormErrorMessage>{formError.email}</FormErrorMessage>}
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Phone</FormLabel>
            <Input type="number" name="phone" value={form.phone} onChange={handleChange} />
          </FormControl>
          {type === UserFormType.Add && (
            <>
              <FormControl isInvalid={formError.password} isRequired>
                <FormLabel>Password</FormLabel>
                <Input type="password" name="password" value={form.password} onChange={handleChange} />
                {formError.password && <FormErrorMessage>{formError.password}</FormErrorMessage>}
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Confirm Password</FormLabel>
                <Input type="password" name="passwordConfirmation" value={form.passwordConfirmation} onChange={handleChange} />
              </FormControl>
            </>
          )}
        </VStack>
        {type === UserFormType.Profile && (
          <VStack gap={4}>
            <FormControl>
              <FormLabel m={0} textAlign="center">Photo</FormLabel>
              <Input ref={photoRef} type="file" name="image" onChange={handleChangePhoto} hidden />
            </FormControl>
            <Image w="250px" h="250px" src={photo} alt="profile photo" />
            <Flex columnGap={2}>
              <Button size="sm" colorScheme="red" onClick={handleRemovePhoto}>Remove</Button>
              <Button size="sm" colorScheme="blue" onClick={() => photoRef.current?.click()}>Change</Button>
            </Flex>
          </VStack>
        )}
      </Grid>
      <Flex columnGap={4} mt={8}>
        <Button type="submit" colorScheme="blue" isLoading={isSubmitting}>{buttonText}</Button>
        {type === UserFormType.Edit && (
          <Button type="button" colorScheme="gray" onClick={onToggleLock} isLoading={isToggling}>{form.active ? 'Lock' : 'Unlock'} User</Button>
        )}
      </Flex>
    </form>
  )
}

UserForm.propTypes = {
  type: PropTypes.oneOf(Object.values(UserFormType)).isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  initialForm: PropTypes.object,
  initialFormError: PropTypes.object,
  isToggling: PropTypes.bool,
  onToggleLock: PropTypes.func
}
